import { useSelector, useDispatch } from 'react-redux';
import { useState, useCallback, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import moment from 'moment' ;

import { authService, confService } from '../../../Services';
import { uploadsUrl } from '../../../Services/config';

import Modal from '../Modal';
import ModalOrgs from '../Modals/Orgs';
import NavMenu from '../NavMenu';

import {ReactComponent as Menu} from '../../../images/icons/General/menu-02.svg';
import {ReactComponent as Mail} from '../../../images/icons/Communication/mail-01.svg';
import {ReactComponent as UserIcon} from '../../../images/icons/Users/user-01.svg';
import {ReactComponent as LogOutIcon} from '../../../images/icons/General/log-out-01.svg';
import {ReactComponent as Switch} from '../../../images/icons/Arrows/switch-horizontal-01.svg';
import {ReactComponent as CalendarIcon} from '../../../images/icons/Time/calendar-minus-01.svg';
import {ReactComponent as Loading} from '../../../images/icons/General/loading-01.svg';

import { setNav, setExpirations } from '../../../redux/stores/orgSlice';
import { setOrgs } from '../../../redux/stores/userSlice';
import { setLocalOrgs } from '../../../redux/stores/localUserSlice';

export const Layout = ({children}) => {
    const [pickOrg, setPickOrg] = useState(false);
    const [openNav, setOpenNav] = useState(false);
    const [loading, setLoading] = useState(false);
    const dispatch = useDispatch();

    const remember = useSelector((state) => state.org.remember);
    const orgId = useSelector((state) => state.org.id);
    const orgParentId = useSelector((state) => state.org.parentId);
    const logoExt = useSelector((state) => state.org.logoExt);
    const orgName = useSelector((state) => state.org.name);
    const version = useSelector((state) => state.org.version);
    const modules = useSelector((state) => state.org.modules);
    const navItems = useSelector((state) => state.org.nav);
    const expirations = useSelector((state) => state.org.expirations);

    const userName = useSelector((state) => state[remember ? 'localUser' : 'user'].name);
    const userPhoto = useSelector((state) => state[remember ? 'localUser' : 'user'].photo);
    const permissions = useSelector((state) => state[remember ? 'localUser' : 'user'].permissions)?.split(',');
    const status = useSelector((state) => state[remember ? 'localUser' : 'user'].status);
    const exhibitDiver = parseInt(useSelector((state) => state[remember ? 'localUser' : 'user'].exhibitDiver));
    const orgs = useSelector((state) => state[remember ? 'localUser' : 'user'].orgs);

    const expirationList = {
        'InsuranceExpiration': 'Diving insurance expiration', 
        'CertExpiration': 'Certification expiration date', 
        'MedicalExpiration': 'Medical Expiration', 
        'NeuroExam': 'Neurological Exam Training Expiration',
        'CPRExpiration': 'CPR Expiration', 
        'O2Expiration': 'O2 Expiration', 
        'FSOExpiration': 'Fill Station Operator (FSO) Expiration', 
        'AEDExpiration': 'AED Expiration', 
        'FirstAidExpiration' : 'First Aid Expiration', 
        'BCServiceExpiration': 'BC Service Expiration', 
        'RegulatorServiceExpiration': 'Regulator Service Expiration', 
        'GaugeCompExp': 'Gauge/Computer Expiration'
    };
    
    // eslint-disable-next-line
    const [events, setEvents] = useState(['click', 'load', 'scroll']);

    let startTimerInterval = useRef();

    let timeChecker = useCallback(() => {
        startTimerInterval.current = setInterval(() => {
            let storedTimeStamp = new Date(sessionStorage.getItem('lastTimeStamp'));

            const diff = moment.duration(moment().diff(moment(storedTimeStamp)));
            const minPast = diff.asMinutes();
            const maxTime = 10;

            if (minPast >= maxTime) {
                sessionStorage.removeItem("lastTimeStamp");
                authService.logout();
            }
        }, 10000);
    }, []);

    let resetTimer = useCallback(() => {
        sessionStorage.setItem('lastTimeStamp', moment());
    }, []);

    let hideExpirations = async () => {
        setLoading(true);
        const res = await authService.setDateExp();

        if (res.res === 'OK') {
            dispatch(setExpirations({}));
            let orgList = orgs.map(org => {
                if (org.id !== orgId)
                    return org;

                let { expirations, ...rest } = org;
                return rest;
            });

            dispatch(remember ? setLocalOrgs(orgList) : setOrgs(orgList));
        }
    
        setLoading(false);
    }

    const handleNavSave = async (updatedItems) => {
        const res = await confService.saveNav(updatedItems);
        if (res.res === 'OK')
            dispatch(setNav(updatedItems));
    };

    useEffect(() => {
        if (!remember) {
            if (!sessionStorage.getItem('lastTimeStamp'))
                resetTimer();

            events.forEach((event) => {
                window.addEventListener(event, resetTimer);
            });

            timeChecker();

            return () => {
                clearInterval(startTimerInterval.current);
            };
        }
    }, [resetTimer, events, timeChecker, remember]);

    return (
        <div className="container">
            <aside>
                <header>
                    {logoExt ? <img src={`${uploadsUrl}/logos/${orgId}${logoExt}`} alt={orgName} /> : <img src={`${uploadsUrl}/logos/aaus.png`} alt={orgName} />}
                    <p>{orgName}</p>
                    <Switch className="switch-icon pointer" onClick={() => setPickOrg(true)} />
                    <Menu className="menu-icon" onClick={() => setOpenNav(!openNav)} />
                </header>
                <nav className={openNav ? 'open' : ''}>
                    <div>
                        <div className="nav-profile">
                            <Link to="/profile"><UserIcon />{userName}</Link>
                            <button onClick={() => authService.logout()}><LogOutIcon /></button>
                        </div>
                        <NavMenu
                            items={navItems}
                            permissions={permissions}
                            modules={modules}
                            exhibitDiver={exhibitDiver}
                            canEdit={permissions.includes('Edit nav') && modules.includes('Pages')}
                            onSave={handleNavSave}
                            setOpenNav={setOpenNav}
                        />
                    </div>
                </nav>
            </aside>
            <div>
                <header>
                    <Link to="/profile">{userPhoto ? <img src={`${uploadsUrl}/users/${userPhoto}?v=${new Date().getTime()}`} alt="User" /> : <UserIcon />}{userName}</Link>
                    <button onClick={() => {
                        authService.logout();
                    }}><LogOutIcon /></button>
                </header>

                {(orgId && orgParentId) && <main key={orgId}>
                    {children}
                </main>}

                <footer>
                    <div>&copy; <strong>Dive Scientific</strong> {new Date().getFullYear()}. <br className="mobile" /><Link to="/versions">Version 2.0.{version}</Link></div>
                    <div><Mail /><a href="mailto:admin@aaus.org" target="_blank" rel="noreferrer">admin@aaus.org</a></div>
                </footer>
            </div>

            {expirations && Object.keys(expirations).length > 0 ? <ModalExpiration hideExpirations={hideExpirations} expirations={expirations} expirationList={expirationList} loading={loading} /> : ''}
            {(!orgParentId || orgParentId === orgId || status !== 1) || pickOrg ? <ModalOrgs pickOrg={pickOrg} setPickOrg={setPickOrg} /> : ''}  
        </div>
    );
};

export default Layout;

const ModalExpiration = ({hideExpirations, expirations, expirationList, loading}) => {
    const today = new Date();
    const threeMonths = new Date();
    threeMonths.setMonth(threeMonths.getMonth() + 3); 

    return (
        <Modal setClose={false} icon={<CalendarIcon />} type="wide">
            <h2>Expirations Alert</h2>
            <p>Attention, you have less than 90 days left to renew the following certifications:</p>
            <div className="expiration-notification">
                {Object.entries(expirations).map(item => {
                    let itemDate = item[1] ? new Date(item[1]).getTime() : 0;
                    return (
                        <div className="expire-date" key={item[0]}>
                            <strong>{expirationList[item[0]]}</strong>
                            <span className={itemDate < today.getTime() ? 'expired' : (itemDate < threeMonths.getTime() ? 'soon' : '')}>{item[1]}</span>
                        </div>
                    )
                })}
            </div>
            <button className="primary" onClick={hideExpirations} disabled={loading}>{loading ? <Loading className="btn-loader" /> : ''}I acknowledge my responsibility</button>
        </Modal>
    )
}